const getModerators = (speakers) => {
  let moderators = speakers.filter((speaker) => speaker.type === "Moderator");

  if (moderators.length > 1) {
    moderators = moderators.map((moderator, index) => {
      return {
        ...moderator,
        // preserve tag, only update name
        name: `${moderator.type} ${index + 1}`,
      };
    });
  } else {
    if (moderators.length) {
      moderators[0] = {
        ...moderators[0],
        name: moderators[0].type,
      };
    }
  }

  return moderators;
};

const getKnownSpeakers = (speakers) => {
  const knownSpeakers = speakers.filter((s) => {
    const isAllInfoAvailable = (s.firstName && s.firstName.trim()) &&
      (s.lastName && s.lastName.trim()) &&
      (s.company && s.company.trim()) &&
      (s.position && s.position.trim());

    return !s.type && isAllInfoAvailable;
  })
    .map((s) => {
      const format = `${s.firstName.trim()} ${s.lastName.trim()} [${s.position.trim()}] (${s.company.trim()})`;

      return { ...s, name: format };
    });

  return knownSpeakers;
};

const getAnalysts = (speakers, speakerTypes = []) => {
  let analysts = speakers.filter((speaker) => speaker.type === "Analyst");

  analysts = analysts.filter((a) => {
    return isValidSpeaker(a.firstName, a.lastName, a.company, a.position, a.type);
  });

  const analystConfig = speakerTypes.find((st) => st.type === "Analyst");
  const useBrackets = analystConfig ? analystConfig.useBrackets : false;

  const wrapType = (label) => useBrackets ? `[${label}]` : label;

  const typeLabelCount = analysts.filter(shouldUseTypeLabel).length;
  let typeLabelIndex = 0;

  const getTypeLabel = (speakerType) => {
    typeLabelIndex += 1;

    if (typeLabelCount > 1) {
      return wrapType(`${speakerType} ${typeLabelIndex}`);
    }

    return wrapType(speakerType);
  };

  analysts = analysts.map((a) => {
    const firstName = a.firstName && a.firstName.trim();
    const lastName = a.lastName && a.lastName.trim();
    const position = a.position && a.position.trim();
    const company = a.company && a.company.trim();

    const hasFullName = firstName && lastName;
    const hasPosition = !!position;
    const hasCompany = !!company;
    const hasCompanyOrPosition = hasPosition || hasCompany;

    if (hasFullName && hasCompanyOrPosition) {
      let format = `${firstName} ${lastName}`;

      if (hasPosition) {
        format += ` [${position}]`;
      } else {
        format += ` ${wrapType(a.type)}`;
      }

      if (hasCompany) {
        format += ` (${company})`;
      }

      return { ...a, name: format };
    }

    if (!hasFullName && hasPosition && hasCompany) {
      const format = `[${position}] (${company})`;

      return { ...a, name: format };
    }

    if (!hasFullName && hasCompany) {
      const format = `${wrapType(a.type)} (${company})`;

      return { ...a, name: format };
    }

    if (!hasFullName && hasPosition) {
      return { ...a, name: getTypeLabel(a.type) };
    }

    return { ...a, name: getTypeLabel(a.type) };
  });

  return analysts;

};

const getCompanyReps = (speakers) => {
  let companyReps = speakers.filter(
    (speaker) => speaker.type === "Company Representative"
  );

  companyReps = companyReps.filter((a) => {
    return isValidSpeaker(a.firstName, a.lastName, a.company, a.position, a.type);
  });

  const typeLabelCount = companyReps.filter(shouldUseTypeLabel).length;
  let typeLabelIndex = 0;

  const getTypeLabel = (speakerType) => {
    typeLabelIndex += 1;

    if (typeLabelCount > 1) {
      return `[${speakerType} ${typeLabelIndex}]`;
    }

    return `[${speakerType}]`;
  };

  companyReps = companyReps.map((a) => {
    const firstName = a.firstName && a.firstName.trim();
    const lastName = a.lastName && a.lastName.trim();
    const position = a.position && a.position.trim();
    const company = a.company && a.company.trim();

    const hasFullName = firstName && lastName;
    const hasPosition = !!position;
    const hasCompany = !!company;
    const hasCompanyOrPosition = hasPosition || hasCompany;

    if (hasFullName && hasCompanyOrPosition) {
      let format = `${firstName} ${lastName}`;

      if (hasPosition) {
        format += ` [${position}]`;
      } else {
        format += ` [${a.type}]`;
      }

      if (hasCompany) {
        format += ` (${company})`;
      }

      return { ...a, name: format };
    }

    if (!hasFullName && hasPosition && hasCompany) {
      const format = `[${position}] (${company})`;

      return { ...a, name: format };
    }

    if (!hasFullName && hasPosition && hasCompany) {
      const format = `[${position}] (${company})`;

      return { ...a, name: format };
    }

    if (!hasFullName && hasCompany) {
      const format = `[${a.type}] (${company})`;

      return { ...a, name: format };
    }

    if (!hasFullName && hasPosition) {
      return { ...a, name: getTypeLabel(a.type) };
    }

    return { ...a, name: getTypeLabel(a.type) };
  });

  return companyReps;
};

const getShareholders = (speakers) => {
  let shareholders = speakers.filter((speaker) => speaker.type === "Shareholder");

  shareholders = shareholders.filter((s) => {
    return isValidSpeaker(s.firstName, s.lastName, s.company, s.position, s.type);
  });

  const typeLabelCount = shareholders.filter(shouldUseTypeLabel).length;
  let typeLabelIndex = 0;

  const getTypeLabel = (speakerType) => {
    typeLabelIndex += 1;

    if (typeLabelCount > 1) {
      return `[${speakerType} ${typeLabelIndex}]`;
    }

    return `[${speakerType}]`;
  };

  shareholders = shareholders.map((s) => {
    const firstName = s.firstName && s.firstName.trim();
    const lastName = s.lastName && s.lastName.trim();
    const position = s.position && s.position.trim();
    const company = s.company && s.company.trim();

    const hasFullName = firstName && lastName;
    const hasPosition = !!position;
    const hasCompany = !!company;
    const hasCompanyOrPosition = hasPosition || hasCompany;

    if (hasFullName && hasCompanyOrPosition) {
      let format = `${firstName} ${lastName}`;

      if (hasPosition) {
        format += ` [${position}]`;
      } else {
        format += ` [${s.type}]`;
      }

      if (hasCompany) {
        format += ` (${company})`;
      }

      return { ...s, name: format };
    }

    if (!hasFullName && hasPosition && hasCompany) {
      const format = `[${position}] (${company})`;

      return { ...s, name: format };
    }

    if (!hasFullName && hasCompany) {
      const format = `[${s.type}] (${company})`;

      return { ...s, name: format };
    }

    if (!hasFullName && hasPosition) {
      return { ...s, name: getTypeLabel(s.type) };
    }

    return { ...s, name: getTypeLabel(s.type) };
  });

  return shareholders;

};

const getVideoNarrators = (speakers) => {
  let videoNarrators = speakers.filter((s) => s.type === "Video Narrator");

  if (videoNarrators.length > 1) {
    videoNarrators = videoNarrators.map((s, index) => {
      return { ...s, name: `[${s.type} ${index + 1}]` };
    });
  } else {
    if (videoNarrators.length) {
      videoNarrators[0] = {
        ...videoNarrators[0],
        name: `[${videoNarrators[0].type}]`,
      };
    }
  }

  return videoNarrators;
};

const getTranslators = (speakers) => {
  let translators = speakers.filter((s) => s.type === "Translator");

  if (translators.length > 1) {
    translators = translators.map((s, index) => {
      return { ...s, name: `[${s.type} ${index + 1}]` };
    });
  } else {
    if (translators.length) {
      translators[0] = {
        ...translators[0],
        name: `[${translators[0].type}]`,
      };
    }
  }

  return translators;
};

const getUnknownSpeakers = (speakers) => {
  let unknownSpeakers = speakers.filter((s) => s.type === "Unknown Speaker");

  if (unknownSpeakers.length > 1) {
    unknownSpeakers = unknownSpeakers.map((s, index) => {
      return { ...s, name: `[${s.type} ${index + 1}]` };
    });
  } else {
    if (unknownSpeakers.length) {
      unknownSpeakers[0] = {
        ...unknownSpeakers[0],
        name: `[${unknownSpeakers[0].type}]`,
      };
    }
  }

  return unknownSpeakers;
};

// Tegus speaker types

const getAdvisors = (speakers) => {
  let advisors = speakers.filter((speaker) => speaker.type === "Advisor");

  if (advisors.length > 1) {
    advisors = advisors.map((advisor, index) => {
      return {
        ...advisor,
        // preserve tag, only update name
        name: `${advisor.type} ${index + 1}`,
      };
    });
  } else {
    if (advisors.length) {
      advisors[0] = {
        ...advisors[0],
        name: advisors[0].type,
      };
    }
  }

  return advisors;
};

const getTegusClients = (speakers) => {
  let clients = speakers.filter((speaker) => speaker.type === "Tegus Client");

  // Even if there are multiple Tegus Client, they will not be numbered
  if (clients.length) {
    clients = clients.map((client) => {
      return {
        ...client,
        // preserve tag, only update name
        name: `${client.type}`,
      };
    });
  }

  return clients;
};

// Third Bridge speaker type
const getSpecialists = (speakers) => {
  let specialists = speakers.filter((speaker) => speaker.type === "Specialist");

  // Even if there are multiple Specialists, they will not be numbered
  if (specialists.length) {
    specialists = specialists.map((specialist) => {
      return {
        ...specialist,
        // preserve tag, only update name
        name: `${specialist.type}`,
      };
    });
  }

  return specialists;
};

export const getFormattedSpeakers = (speakers, speakerTypes = []) => {
  const operator = speakers
    .filter((s) => s.type === "Operator")
    .map((s) => {
      return { ...s, name: s.type };
    });
  const moderators = getModerators(speakers);
  const knownSpeakers = getKnownSpeakers(speakers);
  const analysts = getAnalysts(speakers, speakerTypes);
  const companyReps = getCompanyReps(speakers);
  const shareholders = getShareholders(speakers);
  const unknown = getUnknownSpeakers(speakers);
  const videoNarrators = getVideoNarrators(speakers);
  const translators = getTranslators(speakers);
  const advisors = getAdvisors(speakers);
  const tegusClients = getTegusClients(speakers);
  const specialists = getSpecialists(speakers);

  return operator
    .concat(
      moderators,
      knownSpeakers,
      companyReps,
      analysts,
      shareholders,
      videoNarrators,
      translators,
      advisors,
      tegusClients,
      specialists,
      unknown
    )
    .map((s) => {
      return { tag: s.tag, name: s.name };
    });
};

// Checks for special case brackets and filters them out.
// For example, Analyst 1, Company Representative, Unknown Speaker 2 etc.
export const cleanPosition = (position) => {
  // const analystRegex = /^Analyst[ ]{0,1}\d*$/;
  const companyRepRegex = /^Company Representative[ ]{0,1}\d*$/;
  const videoNarratorRegex = /^Video Narrator[ ]{0,1}\d*$/;
  const translatorRegex = /^Translator[ ]{0,1}\d*$/;
  const unknownSpeakerRegex = /^Unknown Speaker[ ]{0,1}\d*$/;

  if (position.indexOf("Company Representative") > -1) {
    return position.replace(companyRepRegex, "");
  }

  if (position.indexOf("Video Narrator") > -1) {
    return position.replace(videoNarratorRegex, "");
  }

  if (position.indexOf("Translator") > -1) {
    return position.replace(translatorRegex, "");
  }

  if (position.indexOf("Unknown Speaker") > -1) {
    return position.replace(unknownSpeakerRegex, "");
  }

  return position;
};

export const buildSpeakerObject = (speaker, index) => {
  let firstName = speaker.firstName || "";
  let lastName = speaker.lastName || "";
  let company = speaker.company || "";

  // Handle special case for non-bracketed Analyst speaker type (Tegus and Third Bridge)
  if (speaker.type === "Analyst") {
    if (speaker.firstName.trim() === "Analyst") {
      firstName = "";
      lastName = "";
      company = "";
    }
  }

  return {
    id: speaker.tag || `Speaker ${index + 1}`,
    speaker: speaker.name || speaker.speaker || `Speaker ${index + 1}`,
    type: speaker.type || "",
    firstName,
    lastName,
    company,
    position: speaker.position || "",
    tag: speaker.tag || `Speaker ${index + 1}`,
    expanded: false,
  };
};

export const getSpeakerType = (speakerName) => {
  const operatorRegex = /\bOperator(?:\s*\d*)?\b/i;
  const moderatorRegex = /\bModerator(?:\s*\d*)?\b/i;
  const analystRegex = /\bAnalyst(?:\s*\d*)?\b/i;
  const companyRepRegex = /\bCompany\s+Representative(?:\s*\d*)?\b/i;
  const shareholderRegex = /\bShareholder(?:\s*\d*)?\b/i;
  const videoNarratorRegex = /\bVideo\s+Narrator(?:\s*\d*)?\b/i;
  const translatorRegex = /\bTranslator(?:\s*\d*)?\b/i;
  const unknownSpeakerRegex = /\bUnknown\s+Speaker(?:\s*\d*)?\b/i;
  const advisorRegex = /\bAdvisor(?:\s*\d*)?\b/i;
  const tegusClientRegex = /\bTegus\s+Client(?:\s*\d*)?\b/i;
  const specialistRegex = /\bSpecialist(?:\s*\d*)?\b/i;

  if (speakerName.match(operatorRegex)) {
    return "Operator";
  }

  if (speakerName.match(moderatorRegex)) {
    return "Moderator";
  }

  if (speakerName.match(analystRegex)) {
    return "Analyst";
  }

  if (speakerName.match(companyRepRegex)) {
    return "Company Representative";
  }

  if (speakerName.match(shareholderRegex)) {
    return "Shareholder";
  }

  if (speakerName.match(videoNarratorRegex)) {
    return "Video Narrator";
  }

  if (speakerName.match(translatorRegex)) {
    return "Translator";
  }

  if (speakerName.match(unknownSpeakerRegex)) {
    return "Unknown Speaker";
  }

  if (speakerName.match(advisorRegex)) {
    return "Advisor";
  }

  if (speakerName.match(tegusClientRegex)) {
    return "Tegus Client";
  }

  if (speakerName.match(specialistRegex)) {
    return "Specialist";
  }

  return undefined;
};

export const isValidSpeaker = (fname, lname, company, position, type) => {
  const hasType = !!type;
  const isBasicInfoAvailable = ((fname && fname.trim() !== "") &&
    (lname && lname.trim() !== "")) ||
    ((position && position.trim() !== "") ||
      (company && company.trim() !== ""));

  if (type === "Analyst" || type === "Company Representative" || type === "Shareholder") {
    if (hasName({ firstName: fname, lastName: lname })) {
      return company && company.trim() !== "";
    }
  }

  return hasType || isBasicInfoAvailable;
};

// First and last name available, or one of position/company is available
export const hasAllBasicInfo = (fname, lname, company, position) => {
  const isBasicInfoAvailable = ((fname && fname.trim() !== "") &&
    (lname && lname.trim() !== "")) ||
    ((position && position.trim() !== "") ||
      (company && company.trim() !== ""));

  return isBasicInfoAvailable;
};

export const hasName = (speaker) => {
  return (speaker.firstName && speaker.firstName.trim() !== "") &&
    (speaker.lastName && speaker.lastName.trim() !== "");
};

export const hasCompanyOrPosition = (speaker) => {
  return (speaker.company && speaker.company.trim() !== "") ||
    (speaker.position && speaker.position.trim() !== "");
};

const shouldUseTypeLabel = (speaker) => {
  if (hasName(speaker)) {
    return false;
  }

  const hasCompany = speaker.company && speaker.company.trim() !== "";

  return !hasCompany;
};

export const isFinalStage = (workflowStatus, uploadType) => {
  let isFinal = false;

  if (workflowStatus === "Publication") {
    isFinal = true;
  } else if (uploadType === "FIRST_DRAFT" && workflowStatus === "Compliance") {
    isFinal = true;
  }

  return isFinal;
};
